import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import inPreview from "../utils/inPreview";
import isFunction from "../utils/isFunction";
import message from "../utils/message";
import useData from "./useData";
import useMessage, { MESSAGE_TYPES } from "./useMessage";
import useTrack from "./useTrack";
import useMode from "./useMode";
import { MODES } from "../conf";
import useElementDimensions from "./useElementDimensions";
import useImpression from "./useImpression";
import useCachedViewTrack, { TRACKED_TARGET } from "./useCachedViewTrack";
import { SNOWPLOW_EVENT } from "../utils/snowplowEventMigrator";

const useWidget = ({
	onParentStatsUpdate,
	isEmpty = false,
	unmountMessageType = MESSAGE_TYPES.UNMOUNT,
	containerRef,
	resizeOnMount = false,
	canTrackLoad = false, // TODO: [OPTIMIZATION]: ENSURE canTrackLoad also applies to threshold_display / widget_load and not just widget_display
	brandsRemoteKey = "l",
	intersectionId,
} = {}) => {
	const unmounted = useRef(false);
	const [hasBeenViewed, setHasBeenViewed] = useState(false);
	const [isInView, setIsInView] = useState(false);
	const [hasTrackedLoad, setHasTrackedLoad] = useState(false);
	const [shouldResize, setShouldResize] = useState(!!resizeOnMount);

	const {
		scrapedInformation: { purchases } = {},
		[brandsRemoteKey]: brands = [],
		load_metadata: loadMetadata = {},
	} = useData();

	const { mode } = useMode();

	const track = useTrack();

	const updateViewStatus = useCallback(
		({ widget, windowInnerHeight }) => {
			if (isEmpty || !widget) return;
			const {
				boundingClientRect: { top, height },
			} = widget;

			const isParamInView = (param) =>
				param >= 0 && param <= windowInnerHeight;

			const bottom = top + height;
			if (
				isParamInView(top) ||
				isParamInView(bottom) ||
				(top < 0 && bottom > windowInnerHeight)
			) {
				setHasBeenViewed(true);
				setIsInView(true);
			} else {
				setIsInView(false);
			}
		},
		[isEmpty]
	);

	const defaultEventProps = useMemo(
		() => ({
			purchases,
			num_brands: brands && brands.length,
			...loadMetadata,
		}),
		[purchases, brands, loadMetadata]
	);
	useImpression({
		id: intersectionId,
		cacheKey: TRACKED_TARGET.VIEWABLE_THRESHOLD_WIDGET,
		event: SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_DISPLAY,
		properties: defaultEventProps,
		target: containerRef.current,
	});

	useCachedViewTrack({
		id: "PRIMARY_WIDGET", // We can hardcode the id, assuming only one widget (hence one useWidget) in a given session
		key: TRACKED_TARGET.WIDGET,
		event: SNOWPLOW_EVENT.WIDGET_LOAD,
		properties: defaultEventProps,
		canTrack: !isEmpty,
	});

	const trackLoad = useCallback(() => {
		if (!canTrackLoad || hasTrackedLoad) return;
		track(SNOWPLOW_EVENT.WIDGET_DISPLAY, defaultEventProps);
		setHasTrackedLoad(true);
	}, [canTrackLoad, hasTrackedLoad, defaultEventProps, track]);

	useEffect(() => {
		if (!hasBeenViewed) return;
		trackLoad();
	}, [hasBeenViewed, trackLoad]);

	const handleParentStatsUpdate = useCallback(
		(...args) => {
			updateViewStatus(...args);
			if (!isFunction(onParentStatsUpdate)) return;
			onParentStatsUpdate(...args);
		},
		[updateViewStatus, onParentStatsUpdate]
	);
	useMessage(MESSAGE_TYPES.UPDATE_PARENT_STATS, handleParentStatsUpdate);
	useEffect(() => {
		message(MESSAGE_TYPES.REQUEST_PARENT_STATS);
	}, []);

	useEffect(() => {
		if (unmounted.current) {
			return;
		}
		if (!isEmpty) {
			if (mode !== MODES.MODAL) return;
			message(MESSAGE_TYPES.SHOW_MODAL);
			return;
		}
		message(unmountMessageType);
		unmounted.current = true;
	}, [mode, isEmpty, unmountMessageType]);

	const { getBoundingClientRect, getHeight, getWidth } = useElementDimensions(
		{ elementRef: containerRef }
	);

	const triggerResize = useCallback(() => {
		setShouldResize(true);
	}, []);
	const delayedTriggerResize = useCallback(
		(delay) =>
			(...args) =>
				setTimeout(() => triggerResize(...args), delay),
		[triggerResize]
	);

	useEffect(() => {
		if (!shouldResize) return;
		const height = getHeight();
		console.log("Sending height :>> ", height);
		if (height) {
			console.log("height :>> ", height);
			message(MESSAGE_TYPES.RESIZE, { height });
		}
		setShouldResize(false);
	}, [getHeight, shouldResize]);

	const defaultReturnValue = {
		isInView,
		hasBeenViewed,
		isEmpty,
		unmounted,
		getBoundingClientRect,
		getHeight,
		getWidth,
		delayedTriggerResize,
		triggerResize,
	};

	if (inPreview) {
		return {
			...defaultReturnValue,
			isInView: true,
			hasBeenViewed: true,
		};
	}

	return defaultReturnValue;
};

export default useWidget;
